import React, { useState } from 'react';
import { Table, Tooltip } from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import { Link } from 'react-router-dom';
import { EyeOutlined } from '@ant-design/icons';

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

interface DataType {
  [key: string]: any;
}

const TableDetail = ({
  dataSource = [] as any[],
  columnList = [] as any[],
  total = 0,
  currentPage = 1,
  pageSize = 15,
  onPageChange = (page: number, size: number) => {},
}: {
  dataSource?: any[];
  columnList?: any[];
  total?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, size: number) => void;
}) => {

  // Build field->headerName map from sourceColumnWarehouse.customer.list
  const columnTitleMap: Record<string, string> = React.useMemo(() => {
    const map: Record<string, string> = {};
    if (columnList && columnList.length > 0) {
      columnList.forEach((col: any) => {
        if (col.field) map[col.field] = col.headerName || col.name || col.field;
      });
    }
    return map;
  }, [columnList]);

  // Build columns dynamically from data keys
  const columns: TableColumnsType<DataType> = React.useMemo(() => {
    if (!dataSource || dataSource.length === 0) return [];
    const allKeys = new Set<string>();
    dataSource.forEach((item) => {
      Object.keys(item).forEach((k) => {
        if (k !== 'key' && k !== 'id') allKeys.add(k);
      });
    });
    return Array.from(allKeys).map((key) => {
      if (key === 'cus_id') {
        return {
          title: 'Xem hồ sơ',
          dataIndex: key,
          key,
          width: 120,
          render: (value: any) => (
            <Link to={`/customer/pooling-data/customer-list/detail?id_sync=${value}`} target="_blank" rel="noopener noreferrer">
              <EyeOutlined /> Xem
            </Link>
          ),
        };
      }
      return {
        title: columnTitleMap[key] || key,
        dataIndex: key,
        key,
        ellipsis: { showTitle: false },
        render: (value: any) => (
          <Tooltip placement="topLeft" title={value}>
            {value}
          </Tooltip>
        ),
      };
    });
  }, [dataSource, columnTitleMap]);

  const tableData = React.useMemo(() => {
    return dataSource
      .filter((item) => {
        // Filter out empty rows - require at least 2 non-empty meaningful fields
        const meaningfulValues = Object.keys(item).filter(
          (k) => k !== 'key' && k !== 'id' && k !== 'cus_id' && item[k] !== null && item[k] !== undefined && item[k] !== ''
        );
        return meaningfulValues.length >= 2;
      })
      .map((item, idx) => ({
        ...item,
        key: item.id ?? idx,
      }));
  }, [dataSource]);

  return <Table<DataType>
    className="rs-table v2"
    columns={columns}
    dataSource={tableData}
    scroll={{ x: 'max-content' }}
    pagination={{
      current: currentPage,
      pageSize: pageSize,
      total: total,
      showSizeChanger: true,
      pageSizeOptions: ['10', '15', '20', '50'],
      showQuickJumper: true,
      showTotal: (total, range) => `Tổng cộng ${Math.ceil(total / pageSize)} trang`,
      onChange: (page, size) => {
        onPageChange(page, size);
      },
    }}
  />;
};

export default TableDetail;
